<template>
  <div>
    <p class="help-text">
      Schedule multiple occurrences from one calendar event asset.
      After saving, edit individual dates on the <strong>Events</strong> tab.
    </p>

    <div v-if="calendarEventsReady">
      <k-form :columns="10">

         <k-form-section :column-span="10" legend="Event asset">
          <k-form-field label="Calendar event" field-id="recurring-calendar-event">
            <div class="event-picker-row">
              <div class="event-picker-selected" :class="{ 'is-empty': !selectedCalendarEvent }">
                <span v-if="selectedCalendarEvent">
                  {{ selectedCalendarEvent.name }}
                </span>
                <span v-else>
                  Choose a calendar event…
                </span>
              </div>

              <div class="event-picker-actions">
                <button
                  type="button"
                  class="btn btn-outlined"
                  :disabled="recurringCalendarEvents.length === 0"
                  @click="openEventPicker"
                >
                  Choose event
                </button>

                <button
                  v-if="selectedCalendarEventId"
                  type="button"
                  class="btn btn-link"
                  @click="clearSelectedEvent"
                >
                  Clear
                </button>
              </div>
            </div>

            <k-modal
              :show="showEventPickerModal"
              @close="closeEventPicker"
              id="recurring-event-picker-modal"
              role="dialog"
            >
              <template #header>
                <div class="event-picker-modal-header">
                  <h3>Select a calendar event</h3>
                </div>
              </template>

              <template #body>
                <p class="text-muted">
                  Click a row to select an event. Use search to filter by name or tags.
                </p>

                <k-panel :has-content-toggle="false">
                  <template #header>
                    <h4>
                      <i class="fas fa-calendar-day" aria-hidden="true"></i>
                      All calendar events
                    </h4>
                  </template>
                  <template #body>
                    <div class="event-picker-table">
                      <k-table
                        :rows="eventPickerRows"
                        :headers="eventPickerHeaders"
                        :hideable="false"
                        :panel="false"
                        :max="10"
                        :show-controls="false"
                        :support-row-click="true"
                        :sticky-header="true"
                        :empty-message="'No events available'"
                        @row-clicked="onPickEvent"
                      />
                    </div>
                  </template>
                </k-panel>
              </template>

              <template #footer>
                <div class="event-picker-footer">
                  <button type="button" class="btn btn-outlined" @click="closeEventPicker">
                    Cancel
                  </button>
                </div>
              </template>
            </k-modal>
          </k-form-field>
        </k-form-section>

        <k-form-section :column-span="10" legend="Schedule">
          <div
            class="schedule-field-pair"
            :class="{ 'schedule-field-pair--single': isDaily }"
          >
            <k-form-field label="Repeat every" field-id="recurring-interval">
              <div class="repeat-every-controls">
                <k-numeric-input
                  v-model="interval"
                  class="repeat-every-interval"
                  :min="1"
                  :max="8"
                  :step="1"
                  :precision="0"
                  :use-default-messages="false"
                  :disable-clearing="true"
                />
                <k-dropdown
                  id="recurring-interval-unit"
                  class="repeat-every-unit"
                  placeholder="Unit"
                  :options="intervalUnitOptions"
                  :show-search="false"
                  v-model="intervalUnit"
                />
              </div>
              <div class="repeat-every-hints">
                <p class="text-muted">
                  Interval must be between 1 and 8.
                </p>
                <p v-if="isDaily" class="text-muted repeat-every-weekends-hint">
                  Weekends are skipped.
                </p>
              </div>
            </k-form-field>
            <k-form-field
              v-if="!isDaily"
              label="Weekday"
              field-id="recurring-weekday"
            >
              <k-dropdown
                id="recurring-weekday"
                class="full-width"
                placeholder="Weekday"
                :options="weekdayOptions"
                :show-search="false"
                v-model="weekday"
              />
            </k-form-field>
          </div>
          <div class="schedule-field-pair">
            <k-form-field>
              <k-date-picker
                v-model="rangeStartDate"
                label="Starts on"
                input-id="recurring-range-start"
                :required="true"
                :timezone="timezone"
                :model-config="{ timeAdjust: '00:00:00' }"
                :popover="{ visibility: 'focus' }"
              />
            </k-form-field>
            <k-form-field>
              <k-date-picker
                v-model="rangeEndDate"
                label="Ends on"
                input-id="recurring-range-end"
                :required="true"
                :timezone="timezone"
                :model-config="{ timeAdjust: '00:00:00' }"
                :popover="{ visibility: 'focus' }"
              />
              <p class="text-muted">
                <em>Ends on</em> date is excluded when calculating occurrences.
              </p>
              <span
                v-if="rangeStartDate && rangeEndDate && !rangeDateValidation.isValid"
                class="helper-text"
              >{{ rangeDateValidation.message }}</span>
            </k-form-field>
          </div>
          <k-form-field label="Timezone" field-id="recurring-timezone">
            <k-dropdown
              id="recurring-timezone"
              class="full-width"
              :placeholder="timezone"
              search-placeholder="Select timezone"
              :options="timezoneOptions"
              v-model="timezone"
            />
          </k-form-field>
          <div class="schedule-field-pair">
            <k-form-field>
              <k-date-picker
                v-model="startTime"
                label="Start time"
                type="time"
                input-id="recurring-start-time"
                :required="true"
                :timezone="timezone"
                :minute-increment="1"
                :model-config="{ timeAdjust: '00:00:00' }"
                :time-rules="{ minutes: { interval: 1 } }"
                :popover="{ visibility: 'focus' }"
              />
            </k-form-field>
            <k-form-field>
              <k-date-picker
                v-model="endTime"
                label="End time"
                type="time"
                input-id="recurring-end-time"
                :required="true"
                :timezone="timezone"
                :minute-increment="1"
                :model-config="{ timeAdjust: '00:00:00' }"
                :time-rules="{ minutes: { interval: 1 } }"
                :popover="{ visibility: 'focus' }"
              />
            </k-form-field>
          </div>
          <k-form-field
            label="Skip UK bank holidays (England & Wales)"
            field-id="recurring-skip-uk-bank-holidays"
          >
            <k-toggle
              v-model="skipUkBankHolidays"
              input-id="recurring-skip-uk-bank-holidays"
              :round="true"
            />
          </k-form-field>
        </k-form-section>

        <k-form-section :column-span="10" legend="Shared details (all occurrences)">
          <k-form-field label="Live event link" field-id="recurring-live-link">
            <input
              id="recurring-live-link"
              v-model="liveEventLink"
              type="url"
              class="form-control"
              placeholder="https://…"
            >
            <span
              v-if="liveEventLinkTrimmed && !liveEventLinkUrlValidation.isValid"
              class="helper-text"
            >{{ liveEventLinkUrlValidation.message }}</span>
          </k-form-field>

          <div class="schedule-field-pair">
            <k-form-field label="Location" field-id="recurring-location">
              <input id="recurring-location" v-model="location" type="text" class="form-control">
            </k-form-field>
            <k-form-field label="Teams account" field-id="recurring-teams-email">
              <input
                id="recurring-teams-email"
                v-model="teamsEmail"
                type="email"
                class="form-control"
              >
            </k-form-field>
          </div>

          <k-form-field label="Recording links (one per line)" field-id="recurring-recording-links">
            <textarea
              id="recurring-recording-links"
              v-model="recordingLinksRaw"
              class="form-control"
              rows="2"
              placeholder="https://…"
            />
          </k-form-field>

          <k-form-field label="Mandatory" field-id="recurring-mandatory">
            <k-toggle
              v-model="isMandatory"
              input-id="recurring-mandatory"
              :round="true"
            />
          </k-form-field>

          <k-form-field
            label="Groups"
            field-id="recurring-groups-multiselect"
            class="k-form-field--groups"
          >
            <p v-if="!programmeGroups?.length" class="text-muted">
              No programme groups are configured for this programme.
            </p>
            <template v-else>
              <k-dropdown-multi-select
                id="recurring-groups-multiselect"
                v-model="selectedGroupNames"
                class="full-width"
                :options="groupDropdownOptions"
                placeholder="Select groups"
                search-placeholder="Search groups"
                :select-options="false"
                :select-all-on-mount="false"
              />
              <p class="text-muted groups-field-note">
                NOTE: Programme groups are optional - leave empty for all.
              </p>
            </template>
          </k-form-field>

          <div v-if="trainers?.length" class="schedule-field-pair">
            <k-form-field label="Trainers">
              <word-tags
                v-model="selectedTrainerNames"
                label=""
                text-input-placeholder="Trainer name"
                dropdown-placeholder="Add trainer"
                :options="trainerTagOptions"
              />
            </k-form-field>
            <k-form-field label="Primary trainer" field-id="recurring-primary-trainer">
              <k-dropdown
                id="recurring-primary-trainer"
                class="full-width"
                placeholder="None"
                :options="primaryTrainerOptions"
                v-model="primaryTrainerName"
              />
            </k-form-field>
          </div>
        </k-form-section>
      </k-form>

      <div class="form-actions">
        <button
          type="button"
          class="btn btn-success"
          :disabled="saveBusy || !canSave"
          @click="saveReleases"
        >
          Save to module
        </button>
      </div>
    </div>
    <k-loader v-else class="k-loader" />
  </div>
</template>

<style>
#recurring-event-picker-modal .modal-container {
  width: 70vw;
  max-width: 1100px;
}
</style>

<style scoped>
.help-text {
  margin-bottom: 1rem;
}

.form-actions {
  border-top: none;
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 1rem;
  padding-top: 1rem;
}

.full-width {
  width: 100%;
}

.text-muted {
  color: var(--kate-type-alt);
  font-size: 0.9rem;
}

.helper-text {
  font-size: 0.9em;
  color: var(--kate-type-secondary);
  margin: 0;
  font-style: italic;
}

.groups-field-note {
  margin-top: 0.5rem;
  margin-bottom: 0;
  line-height: 1.4;
}

.schedule-field-pair {
  display: grid;
  gap: 0.75rem 15px;
  margin-bottom: 0.75rem;
  grid-template-columns: 1fr;
}

@media (min-width: 768px) {
  .schedule-field-pair {
    grid-template-columns: 1fr 1fr;
  }

  .schedule-field-pair--single {
    grid-template-columns: 1fr;
  }
}

.repeat-every-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.repeat-every-interval {
  width: 7rem;
  flex-shrink: 0;
  margin-bottom: 0;
}

.repeat-every-unit {
  flex: 1;
  min-width: 0;
}

.repeat-every-hints {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  margin-top: 0.35rem;
}

.repeat-every-hints .text-muted {
  margin: 0;
}

.repeat-every-weekends-hint {
  text-align: right;
  margin-left: auto;
}

.k-form-field--groups {
  margin-bottom: 0.25rem;
}

.event-picker-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border: var(--border-tertiary);
  border-radius: 6px;
  padding: 10px;
  background: var(--input-background);
}

.event-picker-selected {
  min-width: 0;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.event-picker-selected.is-empty {
  font-weight: 500;
  color: var(--kate-type-secondary);
}

.event-picker-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.event-picker-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.event-picker-table :deep(.k-table-wrapper) {
  max-height: 60vh;
  overflow: auto;
}

.event-picker-footer {
  display: flex;
  justify-content: flex-end;
}
</style>

<script>
import KDropdown from '@base-components/k-dropdown.vue';
import KDropdownMultiSelect from '@base-components/k-dropdown-multi-select.vue';
import KDatePicker from '@base-components/k-date-picker.vue';
import KLoader from '@base-components/k-loader.vue';
import KNumericInput from '@base-components/k-numeric-input.vue';
import { KForm, KFormField, KFormSection } from '@base-components/forms';
import KToggle from '@base-components/forms/k-toggle.vue';
import KModal from '@base-components/k-modal.vue';
import KPanel from '@base-components/k-panel.vue';
import KTable from '@base-components/tables/display/k-table.vue';
import { formatInTimeZone } from 'date-fns-tz';
import TimeMixin, { defaultTimeZone, defaultTimeOfDay } from '@mixins/time-mixins'; import ModuleTypeMixin from '@mixins/module-type-mixin';
import getOrNull from '@utils/get-or-null';
import WordTags from '../../../components/word-tags.vue';

/** Backend convention: Monday = 0 … Sunday = 6 */
const WEEKDAY_OPTIONS = [
  { id: 0, name: 'Monday' },
  { id: 1, name: 'Tuesday' },
  { id: 2, name: 'Wednesday' },
  { id: 3, name: 'Thursday' },
  { id: 4, name: 'Friday' },
  { id: 5, name: 'Saturday' },
  { id: 6, name: 'Sunday' },
];

const INTERVAL_UNIT_OPTIONS = [
  { id: 'day', name: 'weekday(s)' },
  { id: 'week', name: 'week(s)' },
];

export default {
  name: 'ModuleRecurringCalendarEvents',

  components: {
    KForm,
    KFormField,
    KFormSection,
    KDropdown,
    WordTags,
    KDatePicker,
    KDropdownMultiSelect,
    KToggle,
    KLoader,
    KModal,
    KTable,
    KPanel,
    KNumericInput,
  },

  mixins: [TimeMixin, ModuleTypeMixin],

  props: {
    moduleId: {
      type: Number,
      required: true,
    },
    moduleType: {
      type: String,
      required: true,
    },
    programmeGroups: {
      type: Array,
      default: () => [],
    },
    trainers: {
      type: Array,
      default: () => [],
    },
    moduleReleaseDate: {
      type: String,
      default: undefined,
    },
    moduleCompletionDate: {
      type: String,
      default: undefined,
    },
  },

  data() {
    return {
      calendarEventsReady: false,
      recurringCalendarEvents: [],
      selectedCalendarEventId: undefined,
      showEventPickerModal: false,
      interval: 1,
      intervalUnit: 'week',
      weekday: 0,
      rangeStartDate: undefined,
      rangeEndDate: undefined,
      timezone: defaultTimeZone(),
      startTime: defaultTimeOfDay(9, 0),
      endTime: defaultTimeOfDay(10, 0),
      skipUkBankHolidays: false,
      liveEventLink: '',
      location: '',
      teamsEmail: '',
      recordingLinksRaw: '',
      selectedGroupNames: [],
      selectedTrainerNames: [],
      primaryTrainerName: undefined,
      isMandatory: false,
      saveBusy: false,
      isUrlValid: true,
    };
  },

  computed: {
    liveEventLinkUrlValidation() {
      if (!this.liveEventLinkTrimmed) {
        return { isValid: true, message: '' };
      }
      return this.$validator.urlValidator(this.liveEventLinkTrimmed);
    },
    timezoneOptions() {
      return Intl.supportedValuesOf('timeZone');
    },
    intervalUnitOptions() {
      return INTERVAL_UNIT_OPTIONS;
    },
    isDaily() {
      return this.intervalUnit === 'day';
    },
    weekdayOptions() {
      return WEEKDAY_OPTIONS;
    },
    groupDropdownOptions() {
      return (this.programmeGroups || []).map(g => {
        if (typeof g === 'string') {
          return { id: g, name: g };
        }
        return { id: g.name, name: g.name };
      });
    },
    trainerTagOptions() {
      return (this.trainers || []).map(t => ({ id: t.name, name: t.name }));
    },
    primaryTrainerOptions() {
      return (this.selectedTrainerNames ?? []).map(n => ({ id: n, name: n }));
    },
    releaseEndpoint() {
      return '/api/curriculum/admin/modules/calendar_event/recurring/bulk-release';
    },
    liveEventLinkTrimmed() {
      return (this.liveEventLink || '').trim();
    },
    recordingLinksArray() {
      return (this.recordingLinksRaw || '')
        .split(/\r?\n/)
        .map(s => s.trim())
        .filter(Boolean);
    },
    selectedCalendarEvent() {
      return this.recurringCalendarEvents.find(e => e.id === this.selectedCalendarEventId);
    },
    rangeDateValidation() {
      if (!this.rangeStartDate || !this.rangeEndDate) {
        return { isValid: true, message: '' };
      }

      return this.rangeStartDate.getTime() <= this.rangeEndDate.getTime()
        ? { isValid: true, message: '' }
        : { isValid: false, message: 'End date must be on or after the start date.' };
    },
    canSave() {
      const st = this.startTime;
      const et = this.endTime;
      const timesOk = st instanceof Date && !Number.isNaN(st.getTime())
        && et instanceof Date && !Number.isNaN(et.getTime());
      const rangeOk = this.rangeStartDate && this.rangeEndDate
        && this.rangeStartDate <= this.rangeEndDate;
      const intervalOk = Number.isInteger(this.interval)
        && this.interval >= 1
        && this.interval <= 8;
      return Boolean(
        this.selectedCalendarEventId
          && rangeOk
          && timesOk
          && intervalOk
          && this.liveEventLinkUrlValidation.isValid,
      );
    },

    eventPickerHeaders() {
      return {
        name: {
          name: 'Event',
          type: 'string',
          sortable: true,
          filterable: true,
        },
        tags: {
          name: 'Tags',
          type: 'tags',
          sortable: true,
          filterable: true,
        },
      };
    },

    eventPickerRows() {
      return this.recurringCalendarEvents.map(e => ({
        id: e.id,
        name: e.name,
        tags: { tags: e.tags || [] },
      }));
    },
  },

  mounted() {
    this.initDefaultRange();
    this.loadCalendarEvents();
  },

  methods: {
    openEventPicker() {
      this.showEventPickerModal = true;
    },

    closeEventPicker() {
      this.showEventPickerModal = false;
    },

    clearSelectedEvent() {
      this.selectedCalendarEventId = undefined;
    },

    onPickEvent(row) {
      if (!row) return;
      this.selectedCalendarEventId = row.id;
      this.closeEventPicker();
    },

    initDefaultRange() {
      const start = new Date();
      start.setHours(0, 0, 0, 0);

      const end = this.addToDate(start, { months: 3 });
      end.setHours(0, 0, 0, 0);

      this.rangeStartDate = start;
      this.rangeEndDate = end;
    },

    toYyyyMmDd(dt) {
      return formatInTimeZone(dt, this.timezone, 'yyyy-MM-dd');
    },

    loadCalendarEvents() {
      this.calendarEventsReady = false;
      this.$http
        .get('/api/curriculum/admin/calendar_event?active_only=true')
        .then(result => {
          this.recurringCalendarEvents = Object.values(result.data)[0] || [];
        })
        .catch(err => {
          if (this.$http.errIn(err, [404])) {
            this.$logger.warn('No calendar events found');
            this.recurringCalendarEvents = [];
          } else {
            this.$logger.autowarn('Could not load calendar events', undefined, err);
            this.$ktoast.showError(err);
            this.recurringCalendarEvents = [];
          }
        })
        .then(() => {
          this.calendarEventsReady = true;
        });
    },

    buildTrainerPayload() {
      const selected = getOrNull('selectedTrainerNames', this, []) || [];
      return selected
        .map(name => ({
          ...this.trainers.find(tr => tr.name === name),
          is_primary_trainer: name === this.primaryTrainerName,
        }))
        .filter(t => t.trainer_id != null);
    },

    async saveReleases() {
      if (!this.canSave || !this.selectedCalendarEvent) {
        return;
      }

      const trainerNames = this.selectedTrainerNames || [];
      if (trainerNames.length > 0) {
        const primaries = trainerNames.filter(n => n === this.primaryTrainerName);
        if (!this.primaryTrainerName || primaries.length !== 1) {
          this.$ktoast.warning('Pick exactly one primary trainer from the selected trainers.');
          return;
        }
      }

      this.saveBusy = true;
      try {
        const tPayload = this.buildTrainerPayload();

        const body = {
          bulk_new_calendar_events: true,
          // recurrence / generation (server expands to N rows)
          module_id: this.moduleId,
          calendar_event_id: this.selectedCalendarEvent.id,
          interval_unit: this.intervalUnit,
          interval: this.interval,
          ...(this.isDaily ? {} : { weekday: this.weekday }),
          range_start: this.toYyyyMmDd(this.rangeStartDate),
          range_end: this.toYyyyMmDd(this.rangeEndDate),
          timezone: this.timezone,
          start_time: this.parseTimestampToTime(this.startTime, undefined, undefined, this.timezone),
          end_time: this.parseTimestampToTime(this.endTime, undefined, undefined, this.timezone),
          skip_uk_bank_holidays: this.skipUkBankHolidays,
          // shared across all generated releases
          is_mandatory: this.isMandatory,
          groups: [...this.selectedGroupNames],
          live_event_link: this.liveEventLinkTrimmed || null,
          location: this.location || null,
          recording_link: this.recordingLinksArray.length ? this.recordingLinksArray : null,
          teams_email: this.teamsEmail || null,
          trainers: tPayload.length ? tPayload : null,
        };
        const res = await this.$http.put(this.releaseEndpoint, body);
        this.$ktoast.info('Recurring events saved');
        this.$emit('assets-updated', 'calendar_event', res.data.tasks);
      } catch (err) {
        this.$logger.error('Save recurring calendar events failed', undefined, err);
        this.$ktoast.showError(err);
      } finally {
        this.saveBusy = false;
      }
    },
  },
};
</script>
